import React, { Fragment } from 'react';
import * as Oidc from 'redux-oidc';
import axios, { AxiosResponse } from 'axios';
import { User } from 'oidc-client';
import LoadingSpinner from '../components/loaders/combinedHeaderAndSpinner';
import endpoints from './endpoints';
import AuthenticationManager from './authenticationManager';

interface Authenticator {
    authenticator: Function;
    fetchAccessToken: Function;
    getUserInfo: Function;
    signOut: Function;
    updateUserLoggedIn: Function;
    isAuthenticated: Function;
    getAccessToken: Function;
}

const Authenticator = ((): Authenticator => {
    const ACCESS_TOKEN = 'access_token';
    const authConfig = AuthenticationManager;

    const isBlank = (value: string | null | undefined): boolean => {
        return !(value !== '' && value !== null && value !== undefined);
    };

    const inIframe = (): boolean => {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    };

    const getIdToken = (): string | undefined => {
        const oidcUserKey = `oidc.user:${authConfig.settings.authority ?? ''}:${authConfig.settings.client_id ?? ''}`;
        const oidcUser = sessionStorage.getItem(oidcUserKey);
        if (oidcUser) {
            return JSON.parse(oidcUser).id_token;
        }
        return undefined;
    };

    const updateUserActivity = (activity: string): Promise<AxiosResponse> => {
        const code = {
            activityType: activity
        };
        const idToken = getIdToken();
        const headers = {
            Authorization: `Bearer ${idToken}`
        };
        const axiosPromise = axios
            .create({
                headers
            })
            .post(endpoints.ttcActivityEndPoint, code);

        return axiosPromise;
    };

    const getUserInfo = (): Promise<User> => {
        const idToken = getIdToken();

        const headers = {
            Authorization: `Bearer ${idToken}`
        };
        return axios.get(endpoints.ttcUserInfo, { headers }).then((response) => {
            return response.data;
        });
    };

    const authenticator = (props: any, route: string): null => {
        const accessToken = sessionStorage.getItem(ACCESS_TOKEN);

        if (isBlank(accessToken)) {
            authConfig.signinRedirect();
        } else {
            props.history.push(route);
        }

        return null;
    };

    const fetchAccessToken = (props: any, route: string): JSX.Element => {
        if (inIframe()) {
            Oidc.processSilentRenew();
            return <Fragment />;
        }
        return (
            <Oidc.CallbackComponent
                userManager={authConfig}
                successCallback={
                    (success): void => {
                        sessionStorage.setItem(ACCESS_TOKEN, success.access_token);
                        props.history.push(route);
                    }
                }
            >
                <LoadingSpinner />
            </Oidc.CallbackComponent>
        );
    };

    const clearAccessToken = (): void => {
        sessionStorage.removeItem(ACCESS_TOKEN);
    };

    const getAccessToken = (): string | null => {
        return sessionStorage.getItem(ACCESS_TOKEN);
    };

    const isAuthenticated = (): boolean | '' => {
        return !isBlank(sessionStorage.getItem(ACCESS_TOKEN));
    };

    const signOut = async (): Promise<void> => {
        try {
            updateUserActivity('LOGOUT');
            const singoutUrl = await authConfig.createSignoutRequest({ id_token_hint: getAccessToken() });

            clearAccessToken();
            await authConfig.removeUser();

            (window as any).location.href = singoutUrl.url;
        } catch (error) {
            console.error(error);
            (window as any).location.href = '/';
        }
    };

    const updateUserLoggedIn = (): Promise<AxiosResponse> => {
        return updateUserActivity('LOGIN');
    };

    return {
        getUserInfo,
        authenticator,
        fetchAccessToken,
        signOut,
        updateUserLoggedIn,
        isAuthenticated,
        getAccessToken
    };
})();

export default Authenticator;
